import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Book, Loader2 } from "lucide-react";
import { client } from "@/client";
import type { SermonData } from "@/types/sermon";

interface SermonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SermonModal({ open, onOpenChange }: SermonModalProps) {
  const [sermon, setSermon] = useState<SermonData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !sermon) {
      // 데이터 페칭 전 로딩 상태 설정
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(true);
      client.api.sermons.latest
        .$get()
        .then((res) => res.json())
        .then((data) => {
          setSermon(data as SermonData);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [open, sermon]);

  if (!sermon) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[480px] bg-card border-border rounded-2xl rough-shadow">
          <div className="flex items-center justify-center py-12">
            {loading ? (
              <Loader2 className="size-8 text-primary animate-spin" />
            ) : (
              <p className="text-muted-foreground">설교 정보를 불러올 수 없습니다.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // 타임존 문제 방지를 위해 날짜 문자열 직접 파싱
  const [year, month, day] = sermon.date.split("-").map(Number);
  const formattedDate = `${year}년 ${month}월 ${day}일`;

  // 성경 본문을 절별로 분리
  const verses = sermon.bibleText.split("\n");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] bg-card border-border rounded-2xl rough-shadow max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="size-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground rough-shadow">
              <Book className="size-6" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-display font-bold text-primary">
                {sermon.title}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                {formattedDate} 목요예배
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 pr-1">
          {/* 성경 구절 헤더 */}
          <div className="flex items-center gap-2 pt-2">
            <div className="h-px flex-1 bg-border" />
            <span className="text-primary font-bold text-sm px-2">
              {sermon.bibleReference}
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* 성경 본문 */}
          <div className="bg-secondary/30 rounded-xl p-5 space-y-3">
            {verses.map((verse, index) => {
              const match = verse.match(/^(\d+)\s(.+)/);
              if (match) {
                return (
                  <div key={index} className="flex gap-3">
                    <span className="text-primary font-bold text-sm min-w-[24px] text-right">
                      {match[1]}
                    </span>
                    <p className="text-card-foreground leading-relaxed font-body text-[15px] flex-1">
                      {match[2]}
                    </p>
                  </div>
                );
              }
              return (
                <p key={index} className="text-card-foreground leading-relaxed font-body text-[15px]">
                  {verse}
                </p>
              );
            })}
          </div>

          {/* 설교자 정보 */}
          <div className="flex items-center justify-between pt-2 pb-1">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">
              개역개정
            </span>
            <span className="text-sm text-muted-foreground font-medium">
              {sermon.preacher}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
